import { isSessionExpiredError } from '@traceio-org/auth-ui';
import {
  isPlatformApiError,
  type AdjustmentCreateRequest,
  type AdjustmentList,
  type AdminAuditQuery,
  type AdminBillingPeriodQuery,
  type AdminDomainList,
  type AdminMembersQuery,
  type AdminOrganizationsQuery,
  type AdminPlanVersionsQuery,
  type AdminSupportAccessQuery,
  type AuditPage,
  type BillingDocumentList,
  type ContractChange,
  type ContractCreateRequest,
  type ContractList,
  type DomainCreateRequest,
  type DomainCreated,
  type EmergencyStop,
  type EmergencyStopList,
  type EmergencyStopRequest,
  type EntitlementGrantRequest,
  type EntitlementList,
  type InvitationCreateRequest,
  type InvitationList,
  type MemberList,
  type MembershipChange,
  type OrganizationModules,
  type PeriodUsage,
  type PlanVersionDraftChange,
  type PlanVersionDraftCreateRequest,
  type PlanVersionList,
  type PlatformAdminMethods,
  type PlatformBillingMethods,
  type PlatformClient,
  type ReleaseFlagChange,
  type ReleaseFlagList,
  type SupportAccessGrant,
  type SupportAccessGrantList,
  type SupportAccessRequest,
} from '@traceio-org/platform-client';
import type {
  AdminEnterpriseProvisioning,
  AdminEnterpriseProvisioningRead,
  OrganizationCreateRequest,
  OrganizationCreated,
  AdminHealth,
  AdminPlatformApp,
  AdminPlatformAppDeployment,
  AdminPlatformApps,
  PlatformAppOperationRequest,
  PlatformAppOperationResult,
  AdminKey,
  AdminKeys,
  AdminKeysSummary,
  BillingExemption,
  HealthRange,
  KeyExpiryChange,
  KeyProviderChange,
  KeyTrackingChange,
  Auth0OrganizationLink,
  AdminProductSubscription,
  AdminProductSubscriptionList,
  StaffMember,
  AdminCustomerAccount,
  AdminCustomerAccountList,
  AdminPortal,
  AdminPortalList,
  CustomerAccountChange,
  CustomerAccountCreateRequest,
  PortalInvitationCreate,
  PortalChange,
  PortalCreateRequest,
  StaffMemberChange,
  StaffMemberCreateRequest,
  StaffMemberList,
  StaffInvitation,
  StaffInvitationCreateRequest,
  StaffInvitationList,
  SubscribableProductKey,
  AdminDomain,
  AdminOrganizationDetail,
  AdminOrganizationList,
  Adjustment,
  Contract,
  DOMAIN_ACTIONS,
  Entitlement,
  InvitationSummary,
  Member,
  ModuleCatalog,
  ModuleConfigurationResult,
  PlanVersion,
  ProductKey,
  RatedPeriod,
  ReleaseFlag,
  SelfServeSettings,
} from '@traceio-org/platform-contracts';
import type { AdminTicketMethods } from './deps';
import { createAdminTicketApi, type AdminTicketApi } from './ticketsApi';

/** platform-api refuses longer searches (400); the field stops at the same length. */
export const MAX_SEARCH_LENGTH = 100;

/** Rows per page of the organization list (keyset pagination). */
export const ORGANIZATIONS_PAGE_SIZE = 25;

/** Rows per page of an organization's members (platform-api allows 1 to 100). */
export const MEMBERS_PAGE_SIZE = 25;

/** Events per page of the audit log (platform-api allows 1 to 100). */
export const AUDIT_PAGE_SIZE = 50;

/** platform-api refuses longer member searches (memberListQuerySchema). */
export const MAX_MEMBER_SEARCH_LENGTH = 200;

/** Sign-ups per read of Trace Admin's self-serve list (platform-api allows 1 to 100). */
export const SELF_SERVE_SIGNUPS_LIMIT = 100;

/** The staff list of self-serve sign-ups and one of its rows, as the client parses them (piece 4's schemas). */
export type AdminSelfServeSignupList = Awaited<ReturnType<PlatformClient['adminSelfServeSignups']>>;
export type AdminSelfServeSignup = AdminSelfServeSignupList['items'][number];

/** Usage events per page of a billing period (platform-api allows 1 to 200). */
export const USAGE_EVENTS_PAGE_SIZE = 50;

/** A decision on a pending adjustment, as the two staff routes name it. */
export type AdjustmentDecisionKind = 'approve' | 'reject';

// C0 control characters and DEL. platform-api answers 400 to any of them in a search, so a pasted tab or
// line break becomes a space instead of an error.
// eslint-disable-next-line no-control-regex
const CONTROL_CHARACTERS = /[\u0000-\u001f\u007f]/g;

/** The search as platform-api receives it: control characters as spaces, trimmed, at most `max` characters. */
export function normalizeSearch(text: string, max: number = MAX_SEARCH_LENGTH): string {
  return text.replace(CONTROL_CHARACTERS, ' ').trim().slice(0, max).trim();
}

export interface OrganizationsPageRequest {
  query?: string;
  cursor?: string;
  limit: number;
}

export type DomainAction = (typeof DOMAIN_ACTIONS)[number];

/** One page of members: the search and filters as the person set them, the cursor of the page. */
export interface MembersPageRequest {
  query?: string;
  status?: Member['status'];
  productKey?: ProductKey;
  cursor?: string;
}

/**
 * A module configuration change as Trace Admin sends it: always the whole configuration and a version. `enabled` is
 * left out to leave the module on, off or never configured as it is (contracts 0.24.0).
 */
export interface ModuleChange {
  enabled?: boolean;
  config: Record<string, unknown>;
  expectedVersion: number;
  reason: string;
}

/**
 * The staff reads and writes of Trace Admin. Every call carries an admin-audience token; nothing else is
 * sent. Reads take the signal of the page that asked; writes are never aborted once sent. Reasons are
 * trimmed here, once, so the audit log never records surrounding spaces.
 */
export interface AdminApi extends AdminTicketApi {
  listOrganizations(request: OrganizationsPageRequest, signal?: AbortSignal): Promise<AdminOrganizationList>;
  getOrganization(id: string, signal?: AbortSignal): Promise<AdminOrganizationDetail>;
  listModuleCatalog(signal?: AbortSignal): Promise<ModuleCatalog>;
  getOrganizationModules(organizationId: string, signal?: AbortSignal): Promise<OrganizationModules>;
  updateOrganizationModule(
    organizationId: string,
    moduleKey: string,
    change: ModuleChange,
  ): Promise<ModuleConfigurationResult>;
  listEntitlements(organizationId: string, signal?: AbortSignal): Promise<EntitlementList>;
  grantEntitlement(organizationId: string, request: EntitlementGrantRequest): Promise<Entitlement>;
  listEmergencyStops(organizationId: string, signal?: AbortSignal): Promise<EmergencyStopList>;
  startEmergencyStop(organizationId: string, request: EmergencyStopRequest): Promise<EmergencyStop>;
  releaseEmergencyStop(organizationId: string, stopId: string, reason: string): Promise<EmergencyStop>;
  /**
   * New organization (platform admins): the organization, its Auth0 organization, products, owner invitation and,
   * with Enterprise, its Enterprise provisioning. 409 slug_taken; 409 email_domain_claimed until acknowledged.
   */
  createOrganization(request: OrganizationCreateRequest): Promise<OrganizationCreated>;
  /** The organization's Enterprise provisioning, or { provisioning: null } (every staff role). */
  getEnterpriseProvisioning(
    organizationId: string,
    signal?: AbortSignal,
  ): Promise<AdminEnterpriseProvisioningRead>;
  /** Retry an Enterprise provisioning needing attention (platform admins; reason required). */
  retryEnterpriseProvisioning(organizationId: string, reason: string): Promise<AdminEnterpriseProvisioning>;
  /** Link or sync the organization's Auth0 organization (platform admins; the reason is optional). */
  linkAuth0Organization(organizationId: string, reason: string): Promise<Auth0OrganizationLink>;
  listProductSubscriptions(
    organizationId: string,
    signal?: AbortSignal,
  ): Promise<AdminProductSubscriptionList>;
  /** Subscribe the organization to a product without a billing plan (staff billing managers; reason required). */
  addProductSubscription(
    organizationId: string,
    productKey: SubscribableProductKey,
    reason: string,
  ): Promise<AdminProductSubscription>;
  endProductSubscription(
    organizationId: string,
    subscriptionId: string,
    reason: string,
  ): Promise<AdminProductSubscription>;
  listStaff(signal?: AbortSignal): Promise<StaffMemberList>;
  /**
   * The Health page of this Trace Admin's own environment over one period (every staff role). refresh: a read of a page
   * already open (its automatic refreshes and Check again), which platform-api does not audit again.
   */
  readHealth(range: HealthRange, refresh: boolean, signal?: AbortSignal): Promise<AdminHealth>;
  /** This environment's App Platform apps (every staff role). refresh: a read of the page already open, not audited again. */
  listPlatformApps(refresh: boolean, signal?: AbortSignal): Promise<AdminPlatformApps>;
  /** One App Platform app with its components and deployments (every staff role). */
  readPlatformApp(appId: string, refresh: boolean, signal?: AbortSignal): Promise<AdminPlatformApp>;
  /** One deployment of an app, polled while it runs. */
  readPlatformAppDeployment(
    appId: string,
    deploymentId: string,
    signal?: AbortSignal,
  ): Promise<AdminPlatformAppDeployment>;
  /** Restart, redeploy, stop (archive) or start (restore) an app (platform admins). */
  operatePlatformApp(
    appId: string,
    request: PlatformAppOperationRequest,
  ): Promise<PlatformAppOperationResult>;
  /** This Trace Admin's own environment's keys from Infisical, names only (every staff role; audited per opening). */
  listKeys(signal?: AbortSignal): Promise<AdminKeys>;
  /** The keys' counts by status, for the navigation badge and Health's line (not audited; names no key). */
  readKeysSummary(signal?: AbortSignal): Promise<AdminKeysSummary>;
  /** Record a key's expiry or rotation date (platform admins). */
  setKeyExpiry(project: string, name: string, change: KeyExpiryChange): Promise<AdminKey>;
  /** Choose a key's provider, or null for the name's rules (platform admins). */
  setKeyProvider(project: string, name: string, change: KeyProviderChange): Promise<AdminKey>;
  /** Track this, Not a secret, or null for the name's rules (platform admins). */
  setKeyTracking(project: string, name: string, change: KeyTrackingChange): Promise<AdminKey>;
  listPortals(organizationId: string, signal?: AbortSignal): Promise<AdminPortalList>;
  createPortal(organizationId: string, request: PortalCreateRequest): Promise<AdminPortal>;
  changePortal(organizationId: string, portalId: string, change: PortalChange): Promise<AdminPortal>;
  listCustomerAccounts(organizationId: string, signal?: AbortSignal): Promise<AdminCustomerAccountList>;
  createCustomerAccount(
    organizationId: string,
    request: CustomerAccountCreateRequest,
  ): Promise<AdminCustomerAccount>;
  changeCustomerAccount(
    organizationId: string,
    accountId: string,
    change: CustomerAccountChange,
  ): Promise<AdminCustomerAccount>;
  /** Make a person who has signed in to Trace a staff member (platform admins; reason required). */
  addStaffMember(request: StaffMemberCreateRequest): Promise<StaffMember>;
  changeStaffMember(userId: string, change: StaffMemberChange): Promise<StaffMember>;
  /** The open staff invitations (platform admins). */
  listStaffInvitations(signal?: AbortSignal): Promise<StaffInvitationList>;
  /** Invite an email to become Trace staff: for a person who has never signed in to Trace (platform admins). */
  inviteStaff(request: StaffInvitationCreateRequest): Promise<StaffInvitation>;
  revokeStaffInvitation(invitationId: string, reason: string): Promise<StaffInvitation>;
  listReleaseFlags(signal?: AbortSignal): Promise<ReleaseFlagList>;
  setReleaseFlag(change: ReleaseFlagChange): Promise<ReleaseFlag>;
  listMembers(organizationId: string, request: MembersPageRequest, signal?: AbortSignal): Promise<MemberList>;
  updateMember(organizationId: string, membershipId: string, change: MembershipChange): Promise<Member>;
  listInvitations(organizationId: string, signal?: AbortSignal): Promise<InvitationList>;
  createInvitation(organizationId: string, request: InvitationCreateRequest): Promise<InvitationSummary>;
  createPortalInvitation(
    organizationId: string,
    portalId: string,
    request: PortalInvitationCreate,
  ): Promise<InvitationSummary>;
  revokeInvitation(organizationId: string, invitationId: string, reason: string): Promise<InvitationSummary>;
  listDomains(organizationId: string, signal?: AbortSignal): Promise<AdminDomainList>;
  createDomain(organizationId: string, request: DomainCreateRequest): Promise<DomainCreated>;
  domainAction(
    organizationId: string,
    domainId: string,
    action: DomainAction,
    reason: string,
  ): Promise<AdminDomain>;
  listSupportAccessGrants(
    query: AdminSupportAccessQuery,
    signal?: AbortSignal,
  ): Promise<SupportAccessGrantList>;
  requestSupportAccess(request: SupportAccessRequest): Promise<SupportAccessGrant>;
  revokeSupportAccess(grantId: string, reason: string): Promise<SupportAccessGrant>;
  listAudit(query: AdminAuditQuery, signal?: AbortSignal): Promise<AuditPage>;
  // Billing (milestone 3b): the staff billing routes, read with staff.billing.read, changed with staff.billing.manage.
  listPlanVersions(query: AdminPlanVersionsQuery, signal?: AbortSignal): Promise<PlanVersionList>;
  getPlanVersion(versionId: string, signal?: AbortSignal): Promise<PlanVersion>;
  createPlanVersionDraft(request: PlanVersionDraftCreateRequest): Promise<PlanVersion>;
  updatePlanVersionDraft(versionId: string, change: PlanVersionDraftChange): Promise<PlanVersion>;
  /** Publishes a draft as it was reviewed: confirmDigest is the contentDigest read (412 when it moved). */
  publishPlanVersion(versionId: string, confirmDigest: string, reason: string): Promise<PlanVersion>;
  listContracts(organizationId: string, signal?: AbortSignal): Promise<ContractList>;
  createContract(organizationId: string, request: ContractCreateRequest): Promise<Contract>;
  updateContract(organizationId: string, contractId: string, change: ContractChange): Promise<Contract>;
  /** Ends a contract's running trial now, so billing starts now (409 without a card on file). */
  endContractTrial(
    organizationId: string,
    contractId: string,
    expectedVersion: number,
    reason: string,
  ): Promise<Contract>;
  /** Makes the organization internal (not billed) or bills it again, with a reason (staff.billing.manage). */
  setBillingExemption(organizationId: string, exempt: boolean, reason: string): Promise<BillingExemption>;
  /** The self-serve switch: on or off, when it last changed and who changed it (staff.billing.read). */
  selfServeSettings(signal?: AbortSignal): Promise<SelfServeSettings>;
  /** Turns self-serve sign-up on or off, with a reason (staff.billing.manage). */
  setSelfServe(enabled: boolean, reason: string): Promise<SelfServeSettings>;
  /** The self-serve sign-ups in one state, at most SELF_SERVE_SIGNUPS_LIMIT (staff.billing.read). */
  selfServeSignups(
    state: AdminSelfServeSignup['state'],
    signal?: AbortSignal,
  ): Promise<AdminSelfServeSignupList>;
  /** Runs the setup of a sign-up that needs attention again, with a reason (staff.billing.manage). */
  retrySelfServeSignup(signupId: string, reason: string): Promise<AdminSelfServeSignup>;
  getBillingUsage(
    organizationId: string,
    query: AdminBillingPeriodQuery,
    signal?: AbortSignal,
  ): Promise<PeriodUsage>;
  getBillingLineItems(
    organizationId: string,
    query: AdminBillingPeriodQuery,
    signal?: AbortSignal,
  ): Promise<RatedPeriod>;
  listAdjustments(organizationId: string, signal?: AbortSignal): Promise<AdjustmentList>;
  requestAdjustment(organizationId: string, request: AdjustmentCreateRequest): Promise<Adjustment>;
  decideAdjustment(
    organizationId: string,
    adjustmentId: string,
    decision: AdjustmentDecisionKind,
    reason: string,
  ): Promise<Adjustment>;
  listBillingDocuments(organizationId: string, signal?: AbortSignal): Promise<BillingDocumentList>;
}

export interface AdminApiDependencies {
  client: Pick<
    PlatformClient,
    | 'adminOrganizations'
    | 'adminOrganization'
    | 'adminSelfServeSettings'
    | 'adminUpdateSelfServeSettings'
    | 'adminSelfServeSignups'
    | 'adminRetrySelfServeSignup'
  > &
    Omit<PlatformAdminMethods, 'authorizeAction'> &
    PlatformBillingMethods &
    AdminTicketMethods;
  /** The session DestinationGate confirmed (Auth0 or development identity). */
  session: { getAccessToken(audience: string): Promise<string> };
  /** The registry's admin audience: the only audience platform-api's staff routes accept. */
  audience: string;
  /** The PUT of ticket attachments to their signed storage URL (the browser's fetch by default). */
  upload?: (url: string, init: RequestInit) => Promise<Response>;
}

/**
 * The body of a release or a revocation (reasonBodySchema): the reason is optional there, and an empty one is
 * left out rather than sent as an empty string, which platform-api would refuse.
 */
function reasonBody(reason: string): { reason?: string } {
  const trimmed = reason.trim();
  return trimmed ? { reason: trimmed } : {};
}

/**
 * The staff routes take only admin-audience tokens, so every call asks the session for one first (the
 * sessions cache tokens in memory and refresh them before they expire). The token goes only into the
 * Authorization header of that one request: never into a URL, storage or a log. When the session has no
 * token, the session error propagates and no request is made.
 */
export function createAdminApi({ client, session, audience, upload }: AdminApiDependencies): AdminApi {
  const options = async (signal?: AbortSignal) => ({
    accessToken: await session.getAccessToken(audience),
    ...(signal ? { signal } : {}),
  });
  return {
    ...createAdminTicketApi({ client, options, ...(upload ? { upload } : {}) }),
    async listOrganizations(request, signal) {
      const query: AdminOrganizationsQuery = { limit: request.limit };
      const search = request.query ? normalizeSearch(request.query) : '';
      if (search) query.query = search;
      if (request.cursor) query.cursor = request.cursor;
      return client.adminOrganizations(query, await options(signal));
    },
    async getOrganization(id, signal) {
      return client.adminOrganization(id, await options(signal));
    },
    async listModuleCatalog(signal) {
      return client.adminModules(await options(signal));
    },
    async getOrganizationModules(organizationId, signal) {
      return client.adminOrganizationModules(organizationId, await options(signal));
    },
    async updateOrganizationModule(organizationId, moduleKey, change) {
      return client.adminUpdateOrganizationModule(
        organizationId,
        moduleKey,
        { ...change, reason: change.reason.trim() },
        await options(),
      );
    },
    async listEntitlements(organizationId, signal) {
      return client.adminOrganizationEntitlements(organizationId, await options(signal));
    },
    async grantEntitlement(organizationId, request) {
      return client.adminGrantEntitlement(
        organizationId,
        { ...request, reason: request.reason.trim() },
        await options(),
      );
    },
    async listEmergencyStops(organizationId, signal) {
      return client.adminEmergencyStops(organizationId, await options(signal));
    },
    async startEmergencyStop(organizationId, request) {
      return client.adminStartEmergencyStop(
        organizationId,
        { ...request, reason: request.reason.trim() },
        await options(),
      );
    },
    async releaseEmergencyStop(organizationId, stopId, reason) {
      return client.adminReleaseEmergencyStop(organizationId, stopId, reasonBody(reason), await options());
    },
    async createOrganization(request) {
      return client.adminCreateOrganization(
        {
          ...request,
          displayName: request.displayName.trim(),
          ownerEmail: request.ownerEmail.trim(),
          reason: request.reason.trim(),
        },
        await options(),
      );
    },
    async getEnterpriseProvisioning(organizationId, signal) {
      return client.adminEnterpriseProvisioning(organizationId, await options(signal));
    },
    async retryEnterpriseProvisioning(organizationId, reason) {
      return client.adminRetryEnterpriseProvisioning(
        organizationId,
        { reason: reason.trim() },
        await options(),
      );
    },
    async linkAuth0Organization(organizationId, reason) {
      return client.adminLinkAuth0Organization(organizationId, reasonBody(reason), await options());
    },
    async listProductSubscriptions(organizationId, signal) {
      return client.adminProductSubscriptions(organizationId, await options(signal));
    },
    async addProductSubscription(organizationId, productKey, reason) {
      return client.adminCreateProductSubscription(
        organizationId,
        { productKey, reason: reason.trim() },
        await options(),
      );
    },
    async listPortals(organizationId, signal) {
      return client.adminPortals(organizationId, await options(signal));
    },
    async createPortal(organizationId, request) {
      return client.adminCreatePortal(
        organizationId,
        { ...request, reason: request.reason.trim() },
        await options(),
      );
    },
    async changePortal(organizationId, portalId, change) {
      return client.adminChangePortal(
        organizationId,
        portalId,
        { ...change, reason: change.reason.trim() },
        await options(),
      );
    },
    async listCustomerAccounts(organizationId, signal) {
      return client.adminCustomerAccounts(organizationId, await options(signal));
    },
    async createCustomerAccount(organizationId, request) {
      return client.adminCreateCustomerAccount(
        organizationId,
        { ...request, reason: request.reason.trim() },
        await options(),
      );
    },
    async changeCustomerAccount(organizationId, accountId, change) {
      return client.adminChangeCustomerAccount(
        organizationId,
        accountId,
        { ...change, reason: change.reason.trim() },
        await options(),
      );
    },
    async listStaff(signal) {
      return client.adminStaff(await options(signal));
    },
    async readHealth(range, refresh, signal) {
      return client.adminHealth(refresh ? { range, refresh: true } : { range }, await options(signal));
    },
    async listPlatformApps(refresh, signal) {
      return client.adminPlatformApps(refresh ? { refresh: true } : {}, await options(signal));
    },
    async readPlatformApp(appId, refresh, signal) {
      return client.adminPlatformApp(appId, refresh ? { refresh: true } : {}, await options(signal));
    },
    async readPlatformAppDeployment(appId, deploymentId, signal) {
      return client.adminPlatformAppDeployment(appId, deploymentId, await options(signal));
    },
    async operatePlatformApp(appId, request) {
      return client.adminOperatePlatformApp(appId, request, await options());
    },
    async listKeys(signal) {
      return client.adminKeys(await options(signal));
    },
    async readKeysSummary(signal) {
      return client.adminKeysSummary(await options(signal));
    },
    async setKeyExpiry(project, name, change) {
      return client.adminSetKeyExpiry(project, name, change, await options());
    },
    async setKeyProvider(project, name, change) {
      return client.adminSetKeyProvider(project, name, change, await options());
    },
    async setKeyTracking(project, name, change) {
      return client.adminSetKeyTracking(project, name, change, await options());
    },
    async addStaffMember(request) {
      return client.adminCreateStaffMember({ ...request, reason: request.reason.trim() }, await options());
    },
    async listStaffInvitations(signal) {
      return client.adminStaffInvitations(await options(signal));
    },
    async inviteStaff(request) {
      return client.adminCreateStaffInvitation(
        { ...request, reason: request.reason.trim() },
        await options(),
      );
    },
    async revokeStaffInvitation(invitationId, reason) {
      return client.adminRevokeStaffInvitation(invitationId, { reason: reason.trim() }, await options());
    },
    async changeStaffMember(userId, change) {
      return client.adminChangeStaffMember(
        userId,
        { ...change, reason: change.reason.trim() },
        await options(),
      );
    },
    async endProductSubscription(organizationId, subscriptionId, reason) {
      return client.adminEndProductSubscription(
        organizationId,
        subscriptionId,
        { reason: reason.trim() },
        await options(),
      );
    },
    async listReleaseFlags(signal) {
      return client.adminReleaseFlags(await options(signal));
    },
    async setReleaseFlag(change) {
      return client.adminSetReleaseFlag({ ...change, reason: change.reason.trim() }, await options());
    },
    async listMembers(organizationId, request, signal) {
      const query: AdminMembersQuery = { limit: MEMBERS_PAGE_SIZE };
      const search = request.query ? normalizeSearch(request.query, MAX_MEMBER_SEARCH_LENGTH) : '';
      if (search) query.query = search;
      if (request.status) query.status = request.status;
      if (request.productKey) query.productKey = request.productKey;
      if (request.cursor) query.cursor = request.cursor;
      return client.adminMembers(organizationId, query, await options(signal));
    },
    async updateMember(organizationId, membershipId, change) {
      return client.adminUpdateMember(
        organizationId,
        membershipId,
        { ...change, reason: change.reason.trim() },
        await options(),
      );
    },
    async listInvitations(organizationId, signal) {
      return client.adminInvitations(organizationId, await options(signal));
    },
    async createInvitation(organizationId, request) {
      return client.adminCreateInvitation(
        organizationId,
        { ...request, email: request.email.trim() },
        await options(),
      );
    },
    async createPortalInvitation(organizationId, portalId, request) {
      return client.adminCreatePortalInvitation(
        organizationId,
        portalId,
        { ...request, email: request.email.trim() },
        await options(),
      );
    },
    async revokeInvitation(organizationId, invitationId, reason) {
      return client.adminRevokeInvitation(organizationId, invitationId, reasonBody(reason), await options());
    },
    async listDomains(organizationId, signal) {
      return client.adminDomains(organizationId, await options(signal));
    },
    async createDomain(organizationId, request) {
      return client.adminCreateDomain(organizationId, request, await options());
    },
    async domainAction(organizationId, domainId, action, reason) {
      return client.adminDomainAction(
        organizationId,
        domainId,
        { action, reason: reason.trim() },
        await options(),
      );
    },
    async listSupportAccessGrants(query, signal) {
      return client.adminSupportAccessGrants(query, await options(signal));
    },
    async requestSupportAccess(request) {
      // An empty ticket reference is left out rather than sent as an empty string.
      const { ticketRef, ...rest } = request;
      const ticket = ticketRef?.trim();
      return client.adminRequestSupportAccess(
        { ...rest, reason: request.reason.trim(), ...(ticket ? { ticketRef: ticket } : {}) },
        await options(),
      );
    },
    async revokeSupportAccess(grantId, reason) {
      return client.adminRevokeSupportAccess(grantId, reasonBody(reason), await options());
    },
    async listAudit(query, signal) {
      const request: AdminAuditQuery = { limit: query.limit ?? AUDIT_PAGE_SIZE };
      if (query.organizationId) request.organizationId = query.organizationId;
      const action = query.action?.trim();
      if (action) request.action = action;
      const actorId = query.actorId?.trim();
      if (actorId) request.actorId = actorId;
      if (query.from) request.from = query.from;
      if (query.to) request.to = query.to;
      if (query.cursor) request.cursor = query.cursor;
      return client.adminAudit(request, await options(signal));
    },
    async listPlanVersions(query, signal) {
      const request: AdminPlanVersionsQuery = {};
      if (query.productKey) request.productKey = query.productKey;
      if (query.status) request.status = query.status;
      if (query.organizationId) request.organizationId = query.organizationId;
      return client.adminPlanVersions(request, await options(signal));
    },
    async getPlanVersion(versionId, signal) {
      return client.adminPlanVersion(versionId, await options(signal));
    },
    async createPlanVersionDraft(request) {
      return client.adminCreatePlanVersionDraft(
        { ...request, reason: request.reason.trim() },
        await options(),
      );
    },
    async updatePlanVersionDraft(versionId, change) {
      return client.adminUpdatePlanVersionDraft(
        versionId,
        { ...change, reason: change.reason.trim() },
        await options(),
      );
    },
    async publishPlanVersion(versionId, confirmDigest, reason) {
      return client.adminPublishPlanVersion(
        versionId,
        { confirmDigest, reason: reason.trim() },
        await options(),
      );
    },
    async listContracts(organizationId, signal) {
      return client.adminContracts(organizationId, await options(signal));
    },
    async createContract(organizationId, request) {
      return client.adminCreateContract(
        organizationId,
        { ...request, reason: request.reason.trim() },
        await options(),
      );
    },
    async updateContract(organizationId, contractId, change) {
      return client.adminUpdateContract(
        organizationId,
        contractId,
        { ...change, reason: change.reason.trim() },
        await options(),
      );
    },
    async endContractTrial(organizationId, contractId, expectedVersion, reason) {
      return client.adminEndContractTrial(
        organizationId,
        contractId,
        { expectedVersion, reason: reason.trim() },
        await options(),
      );
    },
    async setBillingExemption(organizationId, exempt, reason) {
      return client.adminSetBillingExemption(
        organizationId,
        { exempt, reason: reason.trim() },
        await options(),
      );
    },
    async selfServeSettings(signal) {
      return client.adminSelfServeSettings(await options(signal));
    },
    async setSelfServe(enabled, reason) {
      return client.adminUpdateSelfServeSettings({ enabled, reason: reason.trim() }, await options());
    },
    async selfServeSignups(state, signal) {
      return client.adminSelfServeSignups({ state, limit: SELF_SERVE_SIGNUPS_LIMIT }, await options(signal));
    },
    async retrySelfServeSignup(signupId, reason) {
      return client.adminRetrySelfServeSignup(signupId, { reason: reason.trim() }, await options());
    },
    async getBillingUsage(organizationId, query, signal) {
      const request: AdminBillingPeriodQuery = { limit: query.limit ?? USAGE_EVENTS_PAGE_SIZE };
      if (query.contractId) request.contractId = query.contractId;
      if (query.periodStart) request.periodStart = query.periodStart;
      if (query.cursor) request.cursor = query.cursor;
      return client.adminBillingUsage(organizationId, request, await options(signal));
    },
    async getBillingLineItems(organizationId, query, signal) {
      const request: AdminBillingPeriodQuery = {};
      if (query.contractId) request.contractId = query.contractId;
      if (query.periodStart) request.periodStart = query.periodStart;
      return client.adminBillingLineItems(organizationId, request, await options(signal));
    },
    async listAdjustments(organizationId, signal) {
      return client.adminAdjustments(organizationId, await options(signal));
    },
    async requestAdjustment(organizationId, request) {
      return client.adminRequestAdjustment(
        organizationId,
        { ...request, reason: request.reason.trim() },
        await options(),
      );
    },
    async decideAdjustment(organizationId, adjustmentId, decision, reason) {
      const body = { reason: reason.trim() };
      return decision === 'approve'
        ? client.adminApproveAdjustment(organizationId, adjustmentId, body, await options())
        : client.adminRejectAdjustment(organizationId, adjustmentId, body, await options());
    },
    async listBillingDocuments(organizationId, signal) {
      return client.adminBillingDocuments(organizationId, await options(signal));
    },
  };
}

/**
 * Why a staff read failed, in the terms the pages act on:
 * - session-ended: no token, or platform-api refused it (expired, revoked, wrong audience): sign in again.
 * - mfa-required: platform-api wants a session established with MFA: sign in again with MFA.
 * - not-staff: the person is no longer active Trace staff.
 * - forbidden: staff, but the staff role lacks the permission for this read.
 * - not-found, invalid-request (for example a page link whose cursor is no longer valid), rate-limited.
 * - unavailable: no answer, an outage or an answer outside the contract; `reference` is the correlation id
 *   sent with the request, for support.
 */
export type AdminFailure =
  | { kind: 'session-ended' }
  | { kind: 'mfa-required' }
  | { kind: 'not-staff' }
  | { kind: 'forbidden' }
  | { kind: 'not-found' }
  | { kind: 'invalid-request' }
  | { kind: 'rate-limited'; reference: string }
  | { kind: 'unavailable'; reference: string | null };

export function classifyAdminFailure(error: unknown): AdminFailure {
  if (isSessionExpiredError(error)) return { kind: 'session-ended' };
  if (!isPlatformApiError(error)) return { kind: 'unavailable', reference: null };
  if (error.kind !== 'http') return { kind: 'unavailable', reference: error.correlationId };
  switch (error.status) {
    case 401:
      return { kind: 'session-ended' };
    case 403:
      // Only the documented reasons widen the answer; a 403 without one stays a plain refusal.
      if (error.problem.reason === 'mfa_required') return { kind: 'mfa-required' };
      if (error.problem.reason === 'staff_required') return { kind: 'not-staff' };
      return { kind: 'forbidden' };
    case 404:
      return { kind: 'not-found' };
    case 400:
    case 422:
      return { kind: 'invalid-request' };
    case 429:
      return { kind: 'rate-limited', reference: error.correlationId };
    default:
      return { kind: 'unavailable', reference: error.correlationId };
  }
}

export function isAbortError(error: unknown): boolean {
  return typeof error === 'object' && error !== null && (error as { name?: unknown }).name === 'AbortError';
}

/**
 * Why a write failed, in words a staff member can act on. A write whose answer never came (no network, an
 * outage) may still have been applied, so it never says that nothing changed. A write platform-api accepted
 * (2xx) whose answer is outside the contract was saved: the page reads the current state again instead of
 * showing an error (and the console says the answer did not match).
 */
export type WriteFailureKind =
  | 'session-ended'
  | 'mfa-required'
  | 'not-staff'
  | 'forbidden'
  | 'not-found'
  | 'conflict'
  | 'invalid'
  | 'rate-limited'
  | 'unknown-outcome'
  | 'saved-unreadable';

export interface WriteFailure {
  kind: WriteFailureKind;
  message: string;
  /** platform-api's own explanation (problem detail), shown as text. */
  detail?: string;
  /** The fields platform-api refused, as "field: message". */
  issues?: string[];
  /** The correlation id of the request, for support. */
  reference?: string | null;
}

const UNKNOWN_OUTCOME =
  'Trace could not confirm the change, so it may not have been saved. Check the current state before you try again.';

export function describeWriteFailure(error: unknown): WriteFailure {
  if (isSessionExpiredError(error)) {
    return {
      kind: 'session-ended',
      message: 'Your session has ended. Sign in again, then repeat the change.',
    };
  }
  if (!isPlatformApiError(error))
    return { kind: 'unknown-outcome', message: UNKNOWN_OUTCOME, reference: null };
  const reference = error.correlationId;
  if (error.kind === 'invalid_response' && error.status >= 200 && error.status < 300) {
    return { kind: 'saved-unreadable', message: 'The change was saved.', reference };
  }
  if (error.kind !== 'http') return { kind: 'unknown-outcome', message: UNKNOWN_OUTCOME, reference };
  const detail = error.problem.detail ? { detail: error.problem.detail } : {};
  switch (error.status) {
    case 401:
      return {
        kind: 'session-ended',
        message: 'Your session has ended. Sign in again, then repeat the change.',
      };
    case 403:
      if (error.problem.reason === 'mfa_required') {
        return {
          kind: 'mfa-required',
          message:
            'Trace Admin needs a session established with multi-factor authentication. Sign in again, then repeat the change.',
        };
      }
      if (error.problem.reason === 'staff_required') {
        return {
          kind: 'not-staff',
          message: 'Trace no longer lists this account as active staff, so the change was refused.',
        };
      }
      return {
        kind: 'forbidden',
        message: 'Trace refused this change: your staff role or support access does not allow it.',
        ...detail,
        reference,
      };
    case 404:
      // Nobody to act on, rather than a record gone from under the page: reloading would not help.
      if (error.problem.reason === 'person_not_found') {
        return { kind: 'not-found', message: 'Trace found nobody to add.', ...detail, reference };
      }
      return {
        kind: 'not-found',
        message: 'Trace could not find this record. It may have been removed; reload the page.',
        ...detail,
        reference,
      };
    case 409:
    case 412:
      // Rules that refuse a change whatever the page shows: say which, never "someone changed this" (which sent
      // people looking for a concurrent edit that never happened, hosted dev 2026-10-07).
      if (error.problem.reason === 'digitalocean_refused') {
        return {
          kind: 'conflict',
          message: 'DigitalOcean refused this, so nothing changed.',
          ...detail,
          reference,
        };
      }
      if (error.problem.reason === 'self_change') {
        return {
          kind: 'conflict',
          message: "You can't change your own access. Ask another administrator.",
          ...detail,
          reference,
        };
      }
      if (error.problem.reason === 'payment_method_required') {
        return {
          kind: 'conflict',
          message:
            'The customer has no card on file. Ask an organization administrator to add one through Manage payment on their billing page, then try again.',
          ...detail,
          reference,
        };
      }
      if (error.problem.reason === 'trial_not_endable') {
        return { kind: 'conflict', message: "This trial can't end early.", ...detail, reference };
      }
      if (error.problem.reason === 'signup_not_retryable') {
        return {
          kind: 'conflict',
          message: 'This sign-up no longer needs attention: its setup is running again or has finished.',
          ...detail,
          reference,
        };
      }
      if (error.problem.reason === 'last_owner') {
        return {
          kind: 'conflict',
          message: 'An organization keeps at least one active owner. Make another member an owner first.',
          ...detail,
          reference,
        };
      }
      return {
        kind: 'conflict',
        message: 'Someone changed this since the page loaded. Check the current state, then try again.',
        ...detail,
        reference,
      };
    case 400:
    case 422: {
      const issues = (error.problem.issues ?? []).map((issue) =>
        issue.path ? `${issue.path}: ${issue.message}` : issue.message,
      );
      return {
        kind: 'invalid',
        message: 'Trace did not accept this change.',
        ...detail,
        ...(issues.length > 0 ? { issues } : {}),
        reference,
      };
    }
    case 429:
      return {
        kind: 'rate-limited',
        message: 'Too many requests. Wait a moment, then try again.',
        reference,
      };
    default:
      return { kind: 'unknown-outcome', message: UNKNOWN_OUTCOME, ...detail, reference };
  }
}
